import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Wordmark } from "./Brand";
import { PhoneLink } from "./PhoneLink";
import { HolidayBanner } from "./HolidayBanner";
import { SITE } from "../data/site";
import { BookingLink } from "./BookingLink";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  // Services opens on a navy hero; keep the cream header backdrop so navy brand/nav text stays legible.
  const solid = scrolled || pathname === "/services";

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        solid ? "bg-cream/90 backdrop-blur-xl border-b border-[#E2DACB]" : "bg-transparent"
      }`}
    >
      <HolidayBanner />
      <nav className="max-w-[1300px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Wordmark tone="dark" />

        <div className="max-md:hidden flex items-center gap-8">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={`link-underline inline-flex items-center min-h-[44px] font-mono text-[14px] uppercase tracking-[0.12em] transition-colors hover:text-navy ${
                pathname === l.to ? "active text-navy" : "text-ink-muted"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <PhoneLink placement="navbar" className="group flex items-center gap-2 min-h-[44px] text-navy hover:text-[#8a6b2f] transition-colors">
            <Phone size={15} className="text-[#8a6b2f]" />
            <span className="font-mono text-[14px] tracking-[0.06em]">{SITE.phoneDisplay}</span>
          </PhoneLink>
          <BookingLink placement="navbar" testId="nav-consultation-btn" className="inline-flex items-center min-h-[44px] bg-navy text-parchment px-6 py-2.5 font-mono text-[13px] uppercase tracking-[0.14em] border border-[#C5A059]/40 hover:bg-navy-2 hover:-translate-y-0.5 transition-all duration-300">
            Book a Consultation
          </BookingLink>
        </div>

        <div className="md:hidden flex items-center gap-3 shrink-0">
          <PhoneLink placement="navbar-mobile" className="flex items-center gap-2 min-h-[44px] min-w-[44px] justify-center text-navy shrink-0">
            <Phone size={16} className="text-[#8a6b2f]" />
            <span className="max-[379px]:hidden font-mono text-[12px] tracking-[0.02em] whitespace-nowrap">{SITE.phoneDisplay}</span>
          </PhoneLink>
          <button
            className="text-navy inline-flex items-center justify-center w-11 h-11 -mr-2"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-cream border-b border-[#E2DACB]"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <Link key={l.to} to={l.to} data-testid={`mobile-nav-${l.label.toLowerCase()}`} className="font-serif text-2xl text-navy">
                  {l.label}
                </Link>
              ))}
              <BookingLink placement="navbar-mobile" testId="mobile-consultation-btn" className="mt-2 inline-flex items-center justify-center min-h-[48px] bg-navy text-parchment px-6 py-3 font-mono text-[14px] uppercase tracking-[0.14em] text-center">
                Book a Consultation
              </BookingLink>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
